/**
 * Iter93 — Log de Actividad: página dedicada que muestra absolutamente
 * todo lo que pasa en la plataforma.
 * Fuentes: activity_log + client_activity_log + finance_entries + ai_usage_log
 *          + payment_transactions + email_events + businesses + generated_sites
 */
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  RefreshCw, Search, ArrowLeft, Bot, Globe, Mail, User,
  ArrowUpCircle, ArrowDownCircle, DollarSign, Sparkles,
  CheckCircle2, ListChecks, Circle, Activity,
} from "lucide-react";
import { authFetch, API } from "../utils/api";

// Etiquetas legibles por event_type (en español)
const TYPE_LABELS = {
  business_created:     "Lead creado",
  lead_received:        "Lead recibido",
  research_done:        "Investigación lista",
  site_generated:       "Sitio generado",
  site_published:       "Sitio publicado",
  sequence_created:     "Secuencia creada",
  sequence_approved:    "Secuencia aprobada",
  email_sent:           "Email enviado",
  email_delivered:      "Email entregado",
  email_opened:         "Email abierto",
  email_bounced:        "Email rebotó",
  email_complained:     "Email marcado como spam",
  payment_received:     "Pago recibido",
  income:               "Ingreso registrado",
  expense:              "Egreso registrado",
  ai_call:              "Llamada IA",
  client_login:         "Login cliente",
  client_chat_accepted: "Cliente aceptó cambio IA",
  client_site_resumed:  "Cliente reactivó sitio",
  client_site_paused:   "Cliente pausó sitio",
  client_snapshot:      "Snapshot de cuenta",
};

const ICON_MAP = {
  bot:    Bot,
  globe:  Globe,
  mail:   Mail,
  user:   User,
  up:     ArrowUpCircle,
  down:   ArrowDownCircle,
  dollar: DollarSign,
  lead:   Sparkles,
  search: Search,
  check:  CheckCircle2,
  list:   ListChecks,
  dot:    Circle,
};

const PAGE_SIZE = 50;

function formatTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const now = new Date();
    const diffMin = Math.floor((now - d) / 60000);
    if (diffMin < 1) return "Ahora";
    if (diffMin < 60) return `Hace ${diffMin} min`;
    if (diffMin < 60 * 24) return `Hace ${Math.floor(diffMin / 60)} h`;
    return d.toLocaleString("es-UY", {
      day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function ActivityLogPage() {
  const navigate = useNavigate();
  const [items, setItems]       = useState([]);
  const [byType, setByType]     = useState({});
  const [total, setTotal]       = useState(0);
  const [loading, setLoading]   = useState(true);
  const [days, setDays]         = useState(30);
  const [filter, setFilter]     = useState("all");      // "all" or specific event_type
  const [search, setSearch]     = useState("");
  const [page, setPage]         = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const typesParam = filter === "all" ? "" : `&types=${encodeURIComponent(filter)}`;
      const r = await authFetch(`${API}/admin/activity-log?days=${days}&limit=500${typesParam}`);
      if (r?.ok) {
        const d = await r.json();
        setItems(d.items || []);
        setByType(d.by_type || {});
        setTotal(d.total || 0);
      }
    } catch {} finally { setLoading(false); }
  }, [days, filter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(0); }, [filter, days, search]);

  const filteredItems = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter(i =>
      (i.title || "").toLowerCase().includes(q) ||
      (i.description || "").toLowerCase().includes(q) ||
      (i.event_type || "").toLowerCase().includes(q)
    );
  }, [items, search]);

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / PAGE_SIZE));
  const pageItems = filteredItems.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // Sorted by_type for filter chips (most frequent first)
  const typeChips = useMemo(() => {
    return Object.entries(byType)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 12);
  }, [byType]);

  return (
    <div className="min-h-screen" style={{ background: "#0e0e0e", fontFamily: "'Inter', sans-serif" }}>
      {/* Sticky header */}
      <header
        className="sticky top-0 z-30"
        style={{
          background: "rgba(14,14,14,0.85)",
          backdropFilter: "blur(20px)",
          borderBottom: "1px solid rgba(255,255,255,0.05)",
        }}
      >
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => navigate(-1)}
              className="w-9 h-9 rounded-xl flex items-center justify-center bg-white/[0.05] hover:bg-white/[0.10] text-gray-300"
              data-testid="activity-log-back-btn"
            >
              <ArrowLeft size={15} />
            </button>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                 style={{ background: "rgba(255,193,7,0.10)", border: "1px solid rgba(255,193,7,0.25)" }}>
              <Activity size={16} style={{ color: "#FFC107" }} />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] font-black uppercase tracking-[0.25em] text-gray-500 mb-0.5">
                Plataforma · Auditoría
              </p>
              <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-white leading-none"
                  style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                Log de actividad
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {[7, 30, 90].map(d => (
              <button
                key={d}
                onClick={() => setDays(d)}
                data-testid={`activity-log-days-${d}`}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${
                  days === d
                    ? "bg-[#FFC107] text-black"
                    : "bg-white/[0.06] text-gray-400 hover:bg-white/[0.10]"
                }`}
              >
                {d}d
              </button>
            ))}
            <button
              onClick={load}
              data-testid="activity-log-refresh-btn"
              className="p-2 rounded-lg bg-white/[0.06] text-gray-400 hover:bg-white/[0.10]"
            >
              <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-5">
        {/* KPIs */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="activity-log-kpis">
          {[
            { label: "Eventos totales", value: total },
            { label: "Tipos distintos", value: Object.keys(byType).length },
            { label: "Ventana", value: `${days} días` },
            { label: "Filtro activo", value: filter === "all" ? "Todos" : (TYPE_LABELS[filter] || filter) },
          ].map((k, i) => (
            <div key={i} className="rounded-2xl p-4"
                 style={{ background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.07)" }}>
              <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-1.5">{k.label}</p>
              <p className="text-2xl font-black text-white truncate">{k.value}</p>
            </div>
          ))}
        </div>

        {/* Filter row */}
        <div className="rounded-2xl p-4 space-y-3"
             style={{ background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.07)" }}>
          <div className="flex items-center gap-2">
            <Search size={14} className="text-gray-500" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Buscar título, descripción o tipo de evento…"
              data-testid="activity-log-search"
              className="flex-1 bg-transparent text-sm text-white placeholder-gray-600 outline-none"
            />
          </div>
          <div className="flex flex-wrap gap-1.5">
            <button
              onClick={() => setFilter("all")}
              data-testid="activity-log-filter-all"
              className={`px-3 py-1 rounded-md text-[11px] font-black transition-colors ${
                filter === "all"
                  ? "bg-[#FFC107] text-black"
                  : "bg-white/[0.04] text-gray-400 border border-white/[0.06] hover:bg-white/[0.08]"
              }`}
            >
              Todos · {total}
            </button>
            {typeChips.map(([type, count]) => (
              <button
                key={type}
                onClick={() => setFilter(type)}
                data-testid={`activity-log-filter-${type}`}
                className={`px-3 py-1 rounded-md text-[11px] font-bold transition-colors ${
                  filter === type
                    ? "bg-purple-500/30 text-purple-200 border border-purple-500/40"
                    : "bg-white/[0.04] text-gray-400 border border-white/[0.06] hover:bg-white/[0.08]"
                }`}
              >
                {TYPE_LABELS[type] || type} · {count}
              </button>
            ))}
          </div>
        </div>

        {/* Feed */}
        <div className="rounded-2xl overflow-hidden"
             style={{ background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.07)" }}>
          {loading && items.length === 0 ? (
            <div className="flex justify-center py-12">
              <RefreshCw size={20} className="animate-spin text-gray-500" />
            </div>
          ) : pageItems.length === 0 ? (
            <p className="text-center text-sm text-gray-500 py-12">Sin eventos en este rango.</p>
          ) : (
            <ul className="divide-y" style={{ borderColor: "rgba(255,255,255,0.05)" }}
                data-testid="activity-log-feed">
              {pageItems.map(item => <ActivityRow key={item.id} item={item} navigate={navigate} />)}
            </ul>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-500 font-bold">
              Página {page + 1} de {totalPages} · {filteredItems.length} eventos
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                data-testid="activity-log-prev"
                className="px-3 py-1.5 rounded-md font-black bg-white/[0.06] hover:bg-white/[0.10] text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                ← Anterior
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                data-testid="activity-log-next"
                className="px-3 py-1.5 rounded-md font-black bg-white/[0.06] hover:bg-white/[0.10] text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                Siguiente →
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function ActivityRow({ item, navigate }) {
  const Icon = ICON_MAP[item.icon_kind] || Circle;
  const label = TYPE_LABELS[item.event_type] || item.event_type;
  return (
    <li
      className={`flex items-start gap-3 px-5 py-3.5 transition-colors ${
        item.link ? "hover:bg-white/[0.03] cursor-pointer" : ""
      }`}
      onClick={() => item.link && navigate(item.link)}
      data-testid={`activity-row-${item.id}`}
    >
      <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5"
           style={{ background: `${item.color}1a`, border: `1px solid ${item.color}40` }}>
        <Icon size={14} style={{ color: item.color }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-wider"
                style={{ background: `${item.color}20`, color: item.color }}>
            {label}
          </span>
          <span className="text-[10.5px] text-gray-500">{formatTime(item.ts)}</span>
        </div>
        <p className="text-sm font-bold text-white mt-1 leading-snug truncate">{item.title}</p>
        {item.description && (
          <p className="text-[11px] text-gray-400 mt-0.5 truncate">{item.description}</p>
        )}
      </div>
    </li>
  );
}
